import React from "react";

// ---------- Palette ----------
// Festival rangoli colors: bright and saturated.
export const RANGOLI_PALETTE = [
  { name: "Marigold", value: "#F5B400" },
  { name: "Saffron", value: "#F49B0B" },
  { name: "Terracotta", value: "#D95D39" },
  { name: "Lotus Red", value: "#C03546" },
  { name: "Rose Pink", value: "#E54B6A" },
  { name: "Magenta", value: "#A02C7E" },
  { name: "Royal Purple", value: "#6A4FB6" },
  { name: "Indigo", value: "#1E2A4F" },
  { name: "Peacock Blue", value: "#1F76A8" },
  { name: "Turquoise", value: "#2DB7A1" },
  { name: "Leaf Green", value: "#3F8E4D" },
  { name: "Lime", value: "#C6D63A" },
  { name: "White Chalk", value: "#FFFFFF" },
  { name: "Earth", value: "#7B3F1D" },
];

// ---------- Stamps ----------
// Each stamp's SVG is drawn centered at (0,0). Color uses currentColor so the
// active swatch can tint the stamp.
const starPoints = (() => {
  const pts = [];
  const r1 = 16, r2 = 7, n = 5;
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? r1 : r2;
    const a = (Math.PI / n) * i - Math.PI / 2;
    pts.push(`${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
})();

export const RANGOLI_STAMPS = [
  {
    id: "dot",
    name: "Dot",
    svg: <circle cx="0" cy="0" r="12" fill="currentColor" />,
  },
  {
    id: "ring",
    name: "Ring",
    svg: <circle cx="0" cy="0" r="13" fill="none" stroke="currentColor" strokeWidth="4" />,
  },
  {
    id: "petal",
    name: "Petal",
    svg: <path d="M 0 14 Q -11 -10 0 -32 Q 11 -10 0 14 Z" fill="currentColor" />,
  },
  {
    id: "leaf",
    name: "Leaf",
    svg: (
      <path
        d="M 0 14 Q 14 -8 0 -28 Q -14 -8 0 14 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    ),
  },
  {
    id: "diamond",
    name: "Diamond",
    svg: <polygon points="0,-18 14,0 0,18 -14,0" fill="currentColor" />,
  },
  {
    id: "triangle",
    name: "Triangle",
    svg: <polygon points="0,-16 14,12 -14,12" fill="currentColor" />,
  },
  {
    id: "star",
    name: "Star",
    svg: <polygon points={starPoints} fill="currentColor" />,
  },
  {
    id: "flower",
    name: "Flower",
    svg: (
      <g>
        {[0, 72, 144, 216, 288].map((a) => (
          <ellipse
            key={a}
            transform={`rotate(${a})`}
            cx="0"
            cy="-11"
            rx="6"
            ry="10"
            fill="currentColor"
          />
        ))}
        <circle cx="0" cy="0" r="5" fill="currentColor" />
      </g>
    ),
  },
  {
    id: "swirl",
    name: "Swirl",
    svg: (
      <path
        d="M -18 8 Q 0 -22 18 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    ),
  },
  {
    id: "heart",
    name: "Heart",
    svg: (
      <path
        d="M 0 -2 C -10 -16, -22 -8, -16 4 C -10 14, 0 18, 0 18 C 0 18, 10 14, 16 4 C 22 -8, 10 -16, 0 -2 Z"
        fill="currentColor"
      />
    ),
  },
];

// ---------- Symmetry options ----------
export const SYMMETRY_OPTIONS = [
  { id: 1, name: "Off", label: "1×" },
  { id: 4, name: "4-fold", label: "4×" },
  { id: 6, name: "6-fold", label: "6×" },
  { id: 8, name: "8-fold", label: "8×" },
  { id: 12, name: "12-fold", label: "12×" },
];

// ---------- Backgrounds ----------
export const RANGOLI_BACKGROUNDS = [
  { name: "Cream", value: "#F9F6F0" },
  { name: "Midnight", value: "#1E2A4F" },
  { name: "Mud", value: "#5E3A24" },
  { name: "Pale rose", value: "#FCE7E0" },
];

// ---------- Facts ----------
export const RANGOLI_FACTS = [
  {
    title: "What is Rangoli?",
    body:
      "Rangoli is the colourful floor art made all across India during festivals — Diwali, Pongal, Onam, weddings, and welcome ceremonies. It's drawn at the doorway to welcome guests and good fortune.",
    emoji: "🪔",
  },
  {
    title: "What is it made of?",
    body:
      "Coloured powders, rice grains, flower petals, turmeric, vermilion, even chalk. Some rangolis are giant masterpieces; some are tiny and made fresh every morning.",
    emoji: "🌸",
  },
  {
    title: "Why so symmetrical?",
    body:
      "Most rangolis are made around a centre point — every pattern repeats and mirrors. The symmetry is calming to draw and beautiful to look at.",
    emoji: "✴️",
  },
  {
    title: "Many names, one art",
    body:
      "It's called rangoli in the north, kolam in Tamil Nadu, muggu in Andhra, alpana in Bengal, mandana in Rajasthan. Same idea, different colours and styles.",
    emoji: "🌏",
  },
];
